import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  signal,
} from '@angular/core';
import { ChartModule } from 'primeng/chart';

import { vigilarColorTextoGrafico } from '../../helpers/color-grafico';
import { SerieChartData } from '../../helpers/grafico-serie';

const FALLBACK_BLUE = '#3b82f6';
const FALLBACK_CELESTE = '#93c5fd';
const ALTO_MINIMO = 8;

function themeColor(variable: string, fallback: string): string {
  if (typeof document === 'undefined') {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  return value || fallback;
}

@Component({
  selector: 'app-grafico-serie',
  standalone: true,
  imports: [ChartModule],
  templateUrl: './grafico-serie.component.html',
  styleUrl: './grafico-serie.component.scss',
})
export class GraficoSerieComponent implements AfterViewInit, OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private readonly texto = vigilarColorTextoGrafico(this.zone);

  readonly type = input.required<'bar' | 'line'>();
  readonly data = input.required<SerieChartData>();

  /** Chart.js mide el contenedor al crearse. Si el flex todavía no cerró, la leyenda queda debajo del eje. */
  readonly listo = signal(false);

  readonly chartData = computed(() => {
    const colors = [
      themeColor('--p-primary-color', FALLBACK_BLUE),
      themeColor('--p-primary-300', FALLBACK_CELESTE),
    ];
    const source = this.data();
    return {
      labels: source.labels,
      datasets: source.datasets.map((dataset, index) => {
        const color = colors[index] ?? dataset.borderColor;
        return {
          ...dataset,
          borderColor: color,
          backgroundColor: color,
          pointBackgroundColor: color,
          pointBorderColor: color,
          borderWidth: 2,
          fill: false,
        };
      }),
    };
  });

  readonly options = computed(() => {
    const color = this.texto.color();
    return {
      color,
      plugins: {
        legend: { position: 'top' as const, labels: { color } },
      },
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { ticks: { color } },
        y: { beginAtZero: true, ticks: { precision: 0, color } },
      },
    };
  });

  private observador: ResizeObserver | null = null;
  private frame = 0;
  private vivo = true;
  private ultimoAlto = 0;
  private ultimoAncho = 0;

  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    this.observador = new ResizeObserver(() => this.programar());
    this.observador.observe(el);
    this.programar();
  }

  ngOnDestroy(): void {
    this.vivo = false;
    this.texto.destruir();
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
    this.observador?.disconnect();
    this.observador = null;
  }

  private programar(): void {
    if (!this.vivo || this.listo() || this.frame) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.intentar();
    });
  }

  private intentar(): void {
    if (!this.vivo || this.listo()) {
      return;
    }
    const el = this.host.nativeElement;
    const alto = el.offsetHeight;
    const ancho = el.offsetWidth;
    if (alto < ALTO_MINIMO || ancho < ALTO_MINIMO) {
      this.ultimoAlto = 0;
      this.ultimoAncho = 0;
      return;
    }
    if (alto !== this.ultimoAlto || ancho !== this.ultimoAncho) {
      this.ultimoAlto = alto;
      this.ultimoAncho = ancho;
      this.programar();
      return;
    }
    this.zone.run(() => this.listo.set(true));
    this.observador?.disconnect();
    this.observador = null;
  }
}
